import { useEffect, useRef, useState } from 'react';
import { Button, Caption1Strong, RadioGroup } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';

import DropdownOptionRow from './DropdownOptionRow';
import { normalizeDropdownOption } from './dropdownFieldUtils';

export type TDropdownChoices = { options: string[]; selectedValue: string };

type TProps = TDropdownChoices & {
  // The options and the selected value come back together, so a change to both is one change.
  onChange: (choices: TDropdownChoices) => void;
};

const newOptionName = (options: string[]) => {
  let number = options.length + 1;
  while (options.includes(`Option ${number}`)) number++;
  return `Option ${number}`;
};

const DropdownChoicesPanel = ({ options, selectedValue, onChange }: TProps) => {
  const refInputs = useRef<(HTMLInputElement | null)[]>([]);
  // A new option, added with Enter or the button, is ready to type over once it renders.
  const [focusIndex, setFocusIndex] = useState<number>();

  useEffect(() => {
    const input = focusIndex === undefined ? undefined : refInputs.current[focusIndex];
    if (!input) return;
    input.select();
    setFocusIndex(undefined);
  }, [focusIndex, options]);

  // The options with `text` at `index`; undefined when the text is blank or another option has it.
  const renamedOptions = (index: number, text: string) => {
    const value = normalizeDropdownOption(text);
    if (!value || options.some((option, i) => i !== index && option === value)) return undefined;
    return options.map((option, i) => (i === index ? value : option));
  };

  // Renaming the default keeps it the default.
  const selectedAfterRename = (index: number, renamed: string[]) =>
    options[index] === selectedValue ? renamed[index] : selectedValue;

  const commitOption = (index: number, text: string) => {
    const renamed = renamedOptions(index, text);
    if (!renamed) return options[index];
    if (renamed[index] !== options[index]) {
      onChange({ options: renamed, selectedValue: selectedAfterRename(index, renamed) });
    }
    return renamed[index];
  };

  const addOption = (index: number, list = options, selected = selectedValue) => {
    onChange({
      options: [...list.slice(0, index), newOptionName(list), ...list.slice(index)],
      selectedValue: selected,
    });
    setFocusIndex(index);
  };

  // Enter commits the option and adds one after it, as one change.
  const commitAndAdd = (index: number, text: string) => {
    const renamed = renamedOptions(index, text);
    addOption(index + 1, renamed ?? options, renamed ? selectedAfterRename(index, renamed) : selectedValue);
    return renamed ? renamed[index] : options[index];
  };

  const removeOption = (index: number) =>
    onChange({
      options: options.filter((_, i) => i !== index),
      selectedValue: options[index] === selectedValue ? '' : selectedValue,
    });

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <Caption1Strong>Options</Caption1Strong>
        <Button
          appearance="transparent"
          size="small"
          disabled={!selectedValue}
          onClick={() => onChange({ options, selectedValue: '' })}
        >
          Clear default
        </Button>
      </div>
      {/* Six 24px rows with 4px gaps and half a seventh, so a longer list visibly scrolls. */}
      <RadioGroup
        className="max-h-[180px] gap-1 overflow-y-auto"
        value={selectedValue}
        onChange={(_, data) => onChange({ options, selectedValue: data.value })}
      >
        {options.map((option, index) => (
          <DropdownOptionRow
            key={index}
            option={option}
            canRemove={options.length > 1}
            refInput={(element) => (refInputs.current[index] = element)}
            onCommit={(text) => commitOption(index, text)}
            onEnter={(text) => commitAndAdd(index, text)}
            onRemove={() => removeOption(index)}
          />
        ))}
      </RadioGroup>
      <Button
        className="self-start !pl-5"
        appearance="transparent"
        size="small"
        icon={<AddRegular />}
        onClick={() => addOption(options.length)}
      >
        Add option
      </Button>
    </div>
  );
};

export default DropdownChoicesPanel;
